import { Injectable } from '@angular/core';
import { ChartHelperUtil } from '@zero/shared/util-helper';
import { ChartSerie, ChartSerieType, ChartType, InstrumentChartModel, TelekursPeriod, Tick } from '@zero/shared/util-models';
import { DepotChartSerie } from '@zero/statistics/domain';
import { parse } from 'date-fns';
import {
  BarPrice,
  ChartOptions,
  ColorType,
  IChartApi,
  ISeriesApi,
  LastPriceAnimationMode,
  LineData,
  LineStyle,
  PriceScaleMode,
  TickMarkType,
  Time,
  UTCTimestamp,
  createChart,
} from 'lightweight-charts';
import { merge } from 'lodash-es';
import { ChartService } from './chart.service';

@Injectable({
  providedIn: 'root',
})
export class TradingViewService extends ChartService {
  #chart: IChartApi;
  #mainLine: ISeriesApi<'Line'>;
  #mainLinePercent: ISeriesApi<'Line'>;

  #askLine: ISeriesApi<'Line'>;
  #askLinePercent: ISeriesApi<'Line'>;

  #bidLine: ISeriesApi<'Line'>;
  #bidLinePercent: ISeriesApi<'Line'>;

  #candleStick: ISeriesApi<'Candlestick'>;
  #area: ISeriesApi<'Area'>;
  #bar: ISeriesApi<'Bar'>;

  #mainSeries: LineData[] = [];
  #bidSeries: LineData[] = [];
  #askSeries: LineData[] = [];

  #previousTick: Tick;
  #activePeriod?: string;

  public setupChart(container: HTMLElement, options: Partial<ChartOptions>) {
    const LOCALE = 'de-DE';
    const mergedOptions = merge(
      {
        layout: {
          fontFamily: '"Work Sans", sans-serif',
          textColor: '#9b9b9b',
          fontSize: 12,
          attributionLogo: false,
        },
        timeScale: {
          minBarSpacing: 0.0001,
          borderVisible: false,
          rightOffset: 7.5,
          color: '#9b9b9b',
          fixLeftEdge: true,
          timeVisible: true,
          secondsVisible: false,
          tickMarkFormatter: (time: UTCTimestamp, tickMarkType: TickMarkType, locale: string) => {
            const date = new Date(Math.floor(new Date(time).getTime() * 1000));
            switch (tickMarkType) {
              case TickMarkType.Year:
                return date.getFullYear();
              case TickMarkType.Month:
                if (this.#activePeriod === TelekursPeriod.OneMonth) {
                  return date.toLocaleDateString(locale, {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                  });
                }

                return date.toLocaleDateString(locale, {
                  month: 'long',
                });
              case TickMarkType.Time:
                return date.toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                });
              case TickMarkType.DayOfMonth:
              default:
                return date.toLocaleDateString(locale, {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit',
                });
            }
          },
        },
        localization: {
          percentageFormatter: (percentage: number) =>
            `${percentage?.toLocaleString(LOCALE, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} %`,
          priceFormatter: (price: BarPrice) =>
            price?.toLocaleString(LOCALE, {
              minimumFractionDigits: price < 1000 ? 2 : 0,
              maximumFractionDigits: 2,
            }),
          timeFormatter: (time: UTCTimestamp) => {
            const date = new Date(Math.floor(new Date(time).getTime() * 1000));
            switch (this.#activePeriod) {
              case TelekursPeriod.SixMonths:
              case TelekursPeriod.OneYear:
              case TelekursPeriod.TwoYears:
              case TelekursPeriod.Maximum:
                return `${date.toLocaleDateString(LOCALE, {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit',
                })}`;
              default:
                return `${date.toLocaleDateString(LOCALE, {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit',
                })} ${date.toLocaleTimeString(LOCALE, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`;
            }
          },
        },
        leftPriceScale: {
          visible: true, // turn on to see percentage on the left side
          mode: PriceScaleMode.Percentage,
          borderVisible: false,
        },
        rightPriceScale: {
          visible: true,
          borderVisible: false,
        },
        grid: {
          horzLines: {
            color: '#eee',
          },
          vertLines: {
            color: '#ffffff',
          },
        },
        crosshair: {
          horzLine: {
            visible: true,
            labelVisible: true,
          },
          vertLine: {
            visible: true,
            style: 0,
            width: 2,
            color: 'rgba(32, 38, 46, 0.1)',
            labelVisible: true,
          },
        },
      },
      options,
    );

    this.#chart = createChart(container, mergedOptions);

    this.#chart.applyOptions({
      handleScale: false,
    });

    this.#mainLinePercent = this.#chart.addLineSeries({
      priceScaleId: 'left',
      color: 'rgba(92, 163, 239, 0)', // Transparent
      lineStyle: LineStyle.Solid,
      lineWidth: 1,
    });

    this.#mainLine = this.#chart.addLineSeries({
      color: 'rgba(92, 163, 239, 1)',
      lineWidth: 2,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
    });
    this.#candleStick = this.#chart.addCandlestickSeries({
      upColor: '#0cae49',
      downColor: '#c91d1d',
    });
    this.#area = this.#chart.addAreaSeries({
      lineColor: 'rgba(92, 163, 239, 1)',
      topColor: 'rgba(92, 163, 239, 1.0)',
      bottomColor: 'rgba(255, 255, 255, 0.0)',
      // @ts-expect-error cant override
      lineWidth: 1.5,
      priceLineStyle: LineStyle.Dashed,
      priceLineColor: '#0cae49',
    });
    this.#bar = this.#chart.addBarSeries({
      upColor: '#0cae49',
      downColor: '#c91d1d',
    });
    this.#bidLine = this.#chart.addLineSeries({
      color: '#FA5165',
      lineWidth: 2,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
    });
    this.#bidLinePercent = this.#chart.addLineSeries({
      priceScaleId: 'left',
      color: 'rgba(92, 163, 239, 0)', // Transparent
      lineStyle: LineStyle.Solid,
      lineWidth: 1,
      priceLineColor: '#FA5165',
      priceLineVisible: false,
    });
    this.#askLine = this.#chart.addLineSeries({
      color: '#37BE90',
      lineWidth: 2,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
    });
    this.#askLinePercent = this.#chart.addLineSeries({
      priceScaleId: 'left',
      color: 'rgba(92, 163, 239, 0)', // Transparent
      lineStyle: LineStyle.Solid,
      lineWidth: 1,
      priceLineColor: '#37BE90',
      priceLineVisible: false,
    });
  }

  public setupMyChart(container: HTMLElement) {
    const LOCALE = 'de-DE';

    this.#chart = createChart(container, {
      layout: {
        fontFamily: '"Work Sans", sans-serif',
        textColor: '#9b9b9b',
        fontSize: 12,
        attributionLogo: false,
      },
      timeScale: {
        minBarSpacing: 0.0001,
        borderVisible: true,
        borderColor: '#979797',
        color: '#9b9b9b',
        fixLeftEdge: true,
        fixRightEdge: true,
        timeVisible: false,
        secondsVisible: false,
        tickMarkFormatter: (time: UTCTimestamp, tickMarkType: TickMarkType, locale: string) => {
          const date = new Date(Math.floor(new Date(time).getTime() * 1000));
          switch (tickMarkType) {
            case TickMarkType.Year:
              return date.getFullYear();
            case TickMarkType.Month:
              return date.toLocaleDateString(locale, {
                month: 'long',
              });
            case TickMarkType.Time:
              return date.toLocaleTimeString(locale, {
                hour: '2-digit',
                minute: '2-digit',
              });
            case TickMarkType.DayOfMonth:
            default:
              return date.toLocaleDateString(locale, {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
              });
          }
        },
      } as unknown,
      leftPriceScale: {
        visible: true,
        borderVisible: false,
      },
      rightPriceScale: {
        visible: false,
      },
      localization: {
        priceFormatter: (price: BarPrice) =>
          price?.toLocaleString(LOCALE, {
            minimumFractionDigits: price < 1000 ? 2 : 0,
            maximumFractionDigits: 2,
          }),
        timeFormatter: (time: UTCTimestamp) => {
          const date = new Date(Math.floor(new Date(time).getTime() * 1000));
          return `${date.toLocaleDateString(LOCALE, {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          })}`;
        },
      },
      grid: {
        horzLines: {
          color: '#eee',
        },
        vertLines: {
          color: '#ffffff',
        },
      },
      crosshair: {
        horzLine: {
          visible: true,
          labelVisible: true,
        },
        vertLine: {
          visible: true,
          style: 0,
          width: 2,
          color: 'rgba(32, 38, 46, 0.1)',
          labelVisible: true,
        },
      },
    });

    this.#area = this.#chart.addAreaSeries({
      lineColor: 'rgba(92, 163, 239, 1)',
      topColor: 'rgba(92, 163, 239, 1.0)',
      bottomColor: 'rgba(255, 255, 255, 0.0)',
      // @ts-expect-error cant override
      lineWidth: 1.5,
      priceLineStyle: LineStyle.Dashed,
    });

    this.#mainLine = this.#chart.addLineSeries({
      color: 'rgba(92, 163, 239, 1)',
      lineWidth: 2,
    });
  }

  public resize(width: number, height: number) {
    this.#chart?.resize(width, height);
  }

  public initMyChart(chartWithoutCash: DepotChartSerie[], chartWithCash: DepotChartSerie[]) {
    if (!chartWithoutCash?.length) {
      this.#area.setData([]);
      this.#mainLine.setData([]);
      return;
    }
    this.#area.setData(chartWithoutCash.map(this.#mapDepotChartSerie));
    this.#mainLine.setData(chartWithCash ? chartWithCash.map(this.#mapDepotChartSerie) : []);
    this.#chart.timeScale().fitContent();

    this.#chart.applyOptions({
      handleScale: {
        mouseWheel: false,
      },
    });
  }

  public toggleCashLine(withCash: boolean, chartWithCash: DepotChartSerie[]) {
    this.#mainLine.setData(withCash ? chartWithCash.map(this.#mapDepotChartSerie) : []);
    this.#chart.timeScale().fitContent();
  }

  public toggleTheme(darkMode: boolean) {
    if (darkMode) {
      this.#chart.applyOptions({
        layout: {
          background: { color: '#1c1c1c', type: ColorType.Solid },
          textColor: 'white',
        },
        grid: {
          horzLines: {
            color: '#383a3e',
          },
          vertLines: {
            color: '#383a3e',
          },
        },
      });
    } else {
      this.#chart.applyOptions({
        layout: {
          background: { color: 'white', type: ColorType.Solid },
          textColor: '#9b9b9b',
        },
        grid: {
          horzLines: {
            color: '#eee',
          },
          vertLines: {
            color: '#ffffff',
          },
        },
      });
    }
  }

  #setMainLinePercent(series: ChartSerie[], isLastSeries: boolean, isIntraday: boolean, prevDayClose: number) {
    if (!isLastSeries) {
      this.#mainLinePercent.setData([]);
      return;
    }

    const data = series.map((serie) => ({
      time: this.#dateToUTCTimestamp(serie.Date),
      value: isIntraday && serie.Last ? serie.Last : serie.Close,
    }));

    if (isIntraday) {
      this.#mainLinePercent.setData(this.#prependPrevDay(data, prevDayClose));
      return;
    }

    this.#mainLinePercent.setData(data);
  }

  #setMainSeries(series: ChartSerie[], isIntraday: boolean, prevDayClose: number) {
    let data = [
      ...series.map((serie) => ({
        time: this.#dateToUTCTimestamp(serie.Date),
        value: isIntraday && serie.Last ? serie.Last : serie.Close,
      })),
    ];

    if (isIntraday) {
      data = this.#prependPrevDay(data, prevDayClose);
    }
    this.#mainSeries = data;
  }

  #setAskSeries(series: ChartSerie[], isIntraday: boolean, prevDayAsk: number) {
    if (!isIntraday) {
      this.#askSeries = [];
      return;
    }

    const data = [
      ...series
        .filter((serie) => serie.Ask)
        .map((serie) => ({
          time: this.#dateToUTCTimestamp(serie.Date),
          value: serie.Ask,
        })),
    ];

    this.#askSeries = this.#prependPrevDay(data, prevDayAsk);
  }

  #setBidSeries(series: ChartSerie[], isIntraday: boolean, prevDayBid: number) {
    if (!isIntraday) {
      this.#bidSeries = [];
      return;
    }

    const data = [
      ...series
        .filter((serie) => serie.Bid)
        .map((serie) => ({
          time: this.#dateToUTCTimestamp(serie.Date),
          value: serie.Bid,
        })),
    ];

    this.#bidSeries = this.#prependPrevDay(data, prevDayBid);
  }

  #prependPrevDay(series: any, prevDayQuote: number) {
    if (!series.length) {
      return [];
    }

    const date = new Date(series[0].time * 1000);
    date.setMilliseconds(date.getMilliseconds() - 1);
    return [
      {
        time: (date.getTime() / 1000) as UTCTimestamp,
        value: prevDayQuote,
      },
      ...series,
    ];
  }

  public toggleSerie(currentSeries: ChartSerieType[]) {
    this.#mainLine.setData(currentSeries.includes('last') ? this.#mainSeries : []);
    this.#mainLinePercent.setData(currentSeries.includes('last') ? this.#mainSeries : []);
    this.#askLine.setData(currentSeries.includes('ask') ? this.#askSeries : []);
    this.#askLinePercent.setData(currentSeries.includes('ask') ? this.#askSeries : []);
    this.#bidLine.setData(currentSeries.includes('bid') ? this.#bidSeries : []);
    this.#bidLinePercent.setData(currentSeries.includes('bid') ? this.#bidSeries : []);

    this.#chart.timeScale().fitContent();
  }

  public initChart(model: InstrumentChartModel) {
    if (!model?.series?.length) {
      return;
    }

    this.#clear();
    this.#previousTick = null;
    this.#activePeriod = model.getActivePeriod();
    const isIntraday = this.#activePeriod === TelekursPeriod.BestAccuracy;

    if (isIntraday) {
      this.#setChartType(model, ChartType.Line);
    }

    const isLastSeries = model.currentSeries.includes('last');
    model.series = ChartHelperUtil.cleanupAndSortChartSeries(model.series, isIntraday);

    model.displaySettings = !isIntraday;

    this.#setMainLinePercent(model.series, isLastSeries, isIntraday, model.prevDayClose);
    this.#setMainSeries(model.series, isIntraday, model.prevDayClose);
    this.#setAskSeries(model.series, isIntraday, model.prevDayAsk);
    this.#setBidSeries(model.series, isIntraday, model.prevDayBid);

    const barAndCandleStick = [
      ...model.series.map((serie) => ({
        time: this.#dateToUTCTimestamp(serie.Date),
        open: serie.Open,
        high: serie.High,
        low: serie.Low,
        close: serie.Close,
      })),
    ];

    switch (model.getActiveType()) {
      case ChartType.Line:
        if (isLastSeries) {
          this.#mainLine.setData(this.#mainSeries);
        }
        if (model?.currentSeries?.includes('ask')) {
          this.#askLine.setData(this.#askSeries);
          this.#askLinePercent.setData(this.#askSeries);
        }
        if (model?.currentSeries?.includes('bid')) {
          this.#bidLine.setData(this.#bidSeries);
          this.#bidLinePercent.setData(this.#bidSeries);
        }

        break;
      case ChartType.Mountain:
        this.#area.setData(this.#mainSeries);
        break;
      case ChartType.HLC:
        this.#bar.setData(barAndCandleStick);
        break;
      case ChartType.Candle:
        this.#candleStick.setData(barAndCandleStick);
        break;
      default:
        break;
    }

    this.#setPercentageColors(this.#mainSeries[0]?.value, this.#mainSeries[this.#mainSeries.length - 1]?.value);
    this.#chart.timeScale().fitContent();
    [TelekursPeriod.Maximum, TelekursPeriod.TwoYears].includes(this.#activePeriod as TelekursPeriod) &&
      this.#chart.timeScale().scrollToRealTime();
  }

  public updateChart(model: InstrumentChartModel) {
    const isValidTick =
      model?.series?.length && model?.tick?.DT && this.#dateToUTCTimestamp(model.series[model.series.length - 1].Date) <= model.tick.DT;
    if (!isValidTick) {
      return;
    }

    const type = model.getActiveType();
    model.isBestAccuracyPossible && this.#cleanupOnNextDayTick(model, type);

    const { tick, currentSeries } = model;
    const isIntraday = this.#activePeriod === TelekursPeriod.BestAccuracy;
    isIntraday ? this.#handleIntraday(tick, type, currentSeries) : this.#updateLastSerie(tick, type, currentSeries);
  }

  #getLastDateOfCurrentSerie(currentSeries: ChartSerieType[]): Date | null {
    const getLastDate = (series: { time: UTCTimestamp }[]): Date | null => {
      if (series.length === 0) {
        return null;
      }
      const time = series[series.length - 1].time;
      return new Date(time * 1000);
    };

    let lastDateOfCurrentSerie: Date | null = null;

    const seriesMap = {
      last: this.#mainSeries,
      ask: this.#askSeries,
      bid: this.#bidSeries,
    };

    currentSeries.forEach((type) => {
      const lastDate = getLastDate(seriesMap[type] as { time: UTCTimestamp }[]);
      if (lastDate && (!lastDateOfCurrentSerie || lastDate > lastDateOfCurrentSerie)) {
        lastDateOfCurrentSerie = lastDate;
      }
    });

    return lastDateOfCurrentSerie;
  }

  #cleanupOnNextDayTick(model: InstrumentChartModel, type: ChartType) {
    const { tick, currentSeries } = model;

    const lastDateOfCurrentSerie = this.#getLastDateOfCurrentSerie(currentSeries);
    const isDifferentDay = lastDateOfCurrentSerie && lastDateOfCurrentSerie.getDate() !== new Date(tick.DT).getDate();

    if (isDifferentDay) {
      const conditions = [
        currentSeries.includes('last') && !tick.Last,
        currentSeries.includes('ask') && currentSeries.includes('bid') && !tick.Ask && !tick.Bid,
        currentSeries.includes('ask') && !tick.Ask,
        currentSeries.includes('bid') && !tick.Bid,
      ];

      if (conditions.some((condition) => condition)) {
        return;
      }

      this.#clear();
      this.#pushSerie(tick, type, currentSeries);
    }
  }

  #updateLastSerie(tick: Tick, type: ChartType, currentSeries: ChartSerieType[]) {
    this.#mainSeries[this.#mainSeries.length - 1].value = tick.Last;
    if (this.#askSeries?.length) {
      this.#askSeries[this.#askSeries.length - 1].value = tick.Ask;
    }
    if (this.#bidSeries?.length) {
      this.#bidSeries[this.#bidSeries.length - 1].value = tick.Bid;
    }

    const isLast = currentSeries.includes('last');
    const isAsk = currentSeries.includes('ask');
    const isBid = currentSeries.includes('bid');

    if (isLast) {
      this.#mainLinePercent.setData([...this.#mainSeries]);

      if (tick?.Last && !isNaN(tick?.Last)) {
        this.#setPercentageColors(this.#mainSeries[0]?.value, tick?.Last);
      }
    }
    if (isAsk) {
      this.#askLinePercent.setData([...this.#askSeries]);
    }
    if (isBid) {
      this.#bidLinePercent.setData([...this.#bidSeries]);
    }

    switch (type) {
      case ChartType.Line:
        if (isLast) {
          this.#mainLine.setData([...this.#mainSeries]);
        }
        if (isAsk) {
          this.#askLine.setData([...this.#askSeries]);
        }
        if (isBid) {
          this.#bidLine.setData([...this.#bidSeries]);
        }
        break;
      case ChartType.Mountain:
        this.#area.setData([...this.#mainSeries]);
        break;
      case ChartType.HLC:
      case ChartType.Candle:
      default:
        break;
    }
  }

  public changeChartType(model: InstrumentChartModel, type: string) {
    this.#setChartType(model, type);
    this.initChart(model);
  }

  #clear() {
    this.#mainLine.setData([]);
    this.#askLine.setData([]);
    this.#bidLine.setData([]);
    this.#candleStick.setData([]);
    this.#area.setData([]);
    this.#bar.setData([]);
    this.#mainLinePercent.setData([]);
    this.#askLinePercent.setData([]);
    this.#bidLinePercent.setData([]);
    this.#mainSeries = [];
    this.#askSeries = [];
    this.#bidSeries = [];
  }

  #setChartType(model: InstrumentChartModel, type: string) {
    model.types = model.types.map((t) => {
      t.isActive = type === t.value;
      return t;
    });
  }

  #dateToUTCTimestamp(date: string): UTCTimestamp {
    const parsedDate = parse(date, 'yyyy-MM-dd HH:mm', new Date());
    const ms = parsedDate.getTime();
    return (ms / 1000) as UTCTimestamp;
  }

  #localeTimestampToUTCTimestamp(ts: number): UTCTimestamp {
    const parsedDate = new Date(ts);
    const ms = parsedDate.getTime() / 1000;
    return ms as UTCTimestamp;
  }

  #handleIntraday(tick: Tick, type: ChartType, currentSeries: ChartSerieType[]) {
    const isLastTickOneMinuteAgo = (tickTime: number): boolean => {
      const minute = 1000 * 60;
      const minuteAgo = Date.now() - minute;
      return Math.abs(tickTime - minuteAgo) >= minute;
    };

    if (!this.#previousTick || isLastTickOneMinuteAgo(this.#previousTick.DT)) {
      this.#pushSerie(tick, type, currentSeries);
    } else {
      this.#updateLastSerie(tick, type, currentSeries);
    }

    this.#chart.timeScale().fitContent();
  }

  #getSerie(value: number, ts: number): LineData<Time> {
    return value
      ? ({
          time: this.#localeTimestampToUTCTimestamp(ts),
          value,
        } as LineData<Time>)
      : null;
  }

  #pushSerie(tick: Tick, type: ChartType, currentSeries: ChartSerieType[]) {
    const mainSerie = this.#getSerie(tick.Last, tick.DT);
    const askSerie = this.#getSerie(tick.Ask, tick.DT);
    const bidSerie = this.#getSerie(tick.Bid, tick.DT);

    mainSerie && this.#mainSeries.push(mainSerie);
    askSerie && this.#askSeries.push(askSerie);
    bidSerie && this.#bidSeries.push(bidSerie);

    const isLast = currentSeries.includes('last');
    const isAsk = currentSeries.includes('ask');
    const isBid = currentSeries.includes('bid');

    if (isLast && mainSerie) {
      this.#mainLinePercent.update(mainSerie);
    }

    switch (type) {
      case ChartType.Line:
        if (isLast && mainSerie) {
          this.#mainLine.update(mainSerie);
        }
        if (isAsk && askSerie) {
          this.#askLine.update(askSerie);
          this.#askLinePercent.update(askSerie);
        }
        if (isBid && bidSerie) {
          this.#bidLine.update(bidSerie);
          this.#bidLinePercent.update(bidSerie);
        }
        break;
      case ChartType.Mountain:
        this.#area.update(mainSerie);
        break;
      case ChartType.HLC:
      case ChartType.Candle:
      default:
        break;
    }
    this.#previousTick = { ...tick };
  }

  #setPercentageColors(firstValue: number, lastValue: number) {
    const red = '#c80808';
    const green = '#0cae49';
    const color = lastValue < firstValue ? red : green;
    const options = {
      priceLineColor: color,
    };
    this.#area.applyOptions(options);
    this.#candleStick.applyOptions(options);
    this.#bar.applyOptions(options);
    this.#mainLine.applyOptions(options);
    this.#mainLinePercent.applyOptions({
      ...options,
      priceLineVisible: false,
    });
  }

  #mapDepotChartSerie(serie: DepotChartSerie) {
    return {
      time: (new Date(serie.date).getTime() / 1000) as UTCTimestamp,
      value: serie.total,
    };
  }
}
